import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { FileDownload, CheckCircle, Schedule } from '@mui/icons-material';

/**
 * Export metadata parsed from agent response
 */
export interface ExportReadyData {
  id: string;
  filename: string;
  format: string;
  title: string;
  expiresAt: string;
}

/**
 * Parse EXPORT_READY marker from message text
 * Returns the export data if found, null otherwise
 */
export function parseExportReady(text: string): ExportReadyData | null {
  // Match JSON object that may contain nested braces - use a more robust pattern
  const regex = /<!-- EXPORT_READY:(\{.*?\}) -->/s;
  const match = text.match(regex);
  
  if (!match) {
    console.log('[parseExportReady] No EXPORT_READY marker found in text');
    return null;
  }
  
  try {
    console.log('[parseExportReady] Found marker, raw JSON:', match[1]);
    const parsed = JSON.parse(match[1]) as ExportReadyData;
    console.log('[parseExportReady] Parsed data:', parsed);
    return parsed;
  } catch (e) {
    console.error('[parseExportReady] Failed to parse EXPORT_READY data:', e, 'Raw:', match[1]);
    return null;
  }
}

/**
 * Parse ALL EXPORT_READY markers from message text (for multiple exports)
 * Returns an array of export data
 */
export function parseAllExportReady(text: string): ExportReadyData[] {
  const results: ExportReadyData[] = [];
  const regex = /<!-- EXPORT_READY:(\{.*?\}) -->/gs;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    try {
      // console.log('[parseAllExportReady] Found marker, raw JSON:', match[1]);
      const parsed = JSON.parse(match[1]) as ExportReadyData;
      // console.log('[parseAllExportReady] Parsed data:', parsed);
      results.push(parsed);
    } catch (e) {
      console.error('[parseAllExportReady] Failed to parse EXPORT_READY data:', e, 'Raw:', match[1]);
    }
  }
  
  if (results.length === 0 && text.includes('EXPORT_READY')) {
    console.warn('[parseAllExportReady] Text contains EXPORT_READY but no valid markers found. Text sample:', text.substring(0, 500));
  }
  
  return results;
}

/**
 * Remove EXPORT_READY marker from text for display
 */
export function stripExportMarker(text: string): string {
  return text.replace(/<!-- EXPORT_READY:\{.*?\} -->\n?/gs, '');
}

interface ExportDownloadButtonProps {
  exportData: ExportReadyData;
}

/**
 * Download button component that appears when agent generates an export
 */
export const ExportDownloadButton: React.FC<ExportDownloadButtonProps> = ({ exportData }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloaded, setDownloaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Check if export has expired - handle invalid dates
  const expiresAtDate = exportData.expiresAt ? new Date(exportData.expiresAt) : null;
  const isValidExpiration = expiresAtDate && !isNaN(expiresAtDate.getTime());
  const isExpired = isValidExpiration ? expiresAtDate < new Date() : false;

  // Get format label
  const formatLabels: Record<string, string> = {
    text: 'Plain Text',
    markdown: 'Markdown',
    html: 'HTML',
    pdf: 'PDF',
    word: 'Word'
  };

  const handleDownload = async () => {
    if (isDownloading || isExpired) return;

    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(`/api/export/download/${exportData.id}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Export has expired or was not found');
        }
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get blob and download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloaded(true);
    } catch (e) {
      console.error('Download error:', e);
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  // Format expiration time - uses the same isValidExpiration from above
  const now = new Date();
  const hoursRemaining = isValidExpiration 
    ? Math.max(0, Math.round((expiresAtDate!.getTime() - now.getTime()) / (1000 * 60 * 60)))
    : 24; // Default to 24 hours if expiration is invalid
  
  // Log export data for debugging
  // React.useEffect(() => {
  //   console.log('[ExportDownloadButton] Export data received:', {
  //     id: exportData.id,
  //     filename: exportData.filename,
  //     format: exportData.format,
  //     expiresAt: exportData.expiresAt,
  //     isValidExpiration,
  //     hoursRemaining
  //   });
  // }, [exportData, isValidExpiration, hoursRemaining]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        my: 1,
        bgcolor: 'action.hover',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        maxWidth: 400
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="success" fontSize="small" />
        <Typography variant="body2" fontWeight={500}>
          {formatLabels[exportData.format] || exportData.format} document ready
        </Typography>
      </Box>

      <Button
        variant="contained"
        startIcon={downloaded ? <CheckCircle /> : <FileDownload />}
        onClick={handleDownload}
        disabled={isDownloading || isExpired}
        size="medium"
        sx={{
          textTransform: 'none',
          fontWeight: 500
        }}
      >
        {isDownloading
          ? 'Downloading...'
          : downloaded
          ? 'Downloaded'
          : `Download "${exportData.filename}"`}
      </Button>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      {!isExpired && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Schedule fontSize="inherit" sx={{ color: 'text.secondary', fontSize: 14 }} />
          <Typography variant="caption" color="text.secondary">
            Link expires in {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {isExpired && (
        <Typography variant="caption" color="error">
          This download link has expired
        </Typography>
      )}
    </Paper>
  );
};

export default ExportDownloadButton;
